import { useCallback, useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import { ArrowLeft, FilePenLine, LockKeyhole, Share2 } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import type { Post } from "../../shared/contracts";
import { PostCard } from "../components/PostCard";
import { CommentsSection } from "../components/CommentsSection";
import { ShareDialog } from "../components/ShareDialog";
import { StatePanel } from "../components/StatePanel";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";

export function PostDetailPage() {
  const { postId = "" } = useParams();
  const loadedPostId = useRef<string | null>(null);
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const { user } = useApp();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api<{ post: Post }>(`/posts/${postId}`);
      setPost(result.post);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "动态详情加载失败");
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    if (!postId || loadedPostId.current === postId) return;
    loadedPostId.current = postId;
    void load();
  }, [load, postId]);

  if (loading) return <div className="page-shell post-detail-page"><StatePanel kind="loading" title="正在打开动态" /></div>;
  if (error || !post) return <div className="page-shell post-detail-page"><StatePanel kind="error" title="无法打开这条动态" description={error ?? "动态不存在"} onRetry={() => void load()} /></div>;
  const isAuthor = user?.id === post.author.id;
  const canShare = post.visibility === "public" && Boolean(post.shareToken);
  const shareUrl = canShare ? `${window.location.origin}/share/posts/${post.shareToken}` : "";

  return (
    <div className="page-shell post-detail-page">
      <header className="post-detail-header">
        <Button component={Link} to="/square" variant="text" color="inherit" startIcon={<ArrowLeft aria-hidden="true" />}>返回动态广场</Button>
        {isAuthor ? <div className="post-detail-actions"><Button component={Link} to={`/posts/${post.id}/edit`} variant="outlined" type="button" startIcon={<FilePenLine aria-hidden="true" />}>编辑动态</Button></div> : null}
      </header>
      {post.listingState === "sold" ? <Alert severity="info">该内容已由发布者标记为已售出，保留展示仅供浏览参考。</Alert> : null}
      {post.visibility === "self" ? <Alert severity="info" icon={<LockKeyhole />}>这是私有动态，仅你和平台管理员可以查看，不会出现在动态广场、个人主页或分享页面。</Alert> : null}
      <PostCard initialPost={post} detail contentAction={canShare ? <Button className="post-share-action" variant="outlined" size="small" type="button" startIcon={<Share2 aria-hidden="true" />} onClick={() => setShareOpen(true)}>分享动态</Button> : undefined} />
      <CommentsSection postId={post.id} />
      {canShare ? <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={post.gameName ? `${post.gameName} · ${post.author.nickname}` : `${post.author.nickname}的动态`} description={post.intro.slice(0, 100)} url={shareUrl} /> : null}
    </div>
  );
}
